import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromStore from '@appStore/metering-points';
import * as fromCustomerStore from '@appStore/customers';
import * as fromAccountStore from '@appStore/account';
import * as fromContractsStore from '@appStore/contracts';
import * as fromAnalyzeStore from '@appStore/analyze';
import { Observable } from 'rxjs';
import { MeteringPoint, Customer, UserInfo, Contract, AnalyzePeriodData } from '@model';
import { filter, switchMap, map, take, tap } from 'rxjs/operators';
import * as combinedSelectors from '@appStore/metering-points/combined-selectors';
import { SubSink } from 'subsink';
import { AnalyzeSettingsType, DatePeriodType, MeteringType, UserType } from '@enums';
import { ActivatedRoute } from '@angular/router';
import { MeteringPointTabType } from 'app/models/enums/metering-point-tab-type.enum';

@Component({
    selector: 'app-metering-point-display',
    templateUrl: './metering-point-display.component.html',
    standalone: false
})
export class MeteringPointDisplayComponent implements OnInit, OnDestroy {
  meteringPoint$: Observable<MeteringPoint>;
  meteringPoints$: Observable<MeteringPoint[]>;
  customer$: Observable<Customer>;
  selectedIndex$: Observable<number>;
  allMeteringPoints$: Observable<MeteringPoint[]>;
  contract$: Observable<Contract>;
  periodData$: Observable<AnalyzePeriodData>;

  private subs = new SubSink();
  hotMeteringPointsCount: number;
  coldMeteringPointsCount: number;
  allMeteringPointsCount: number;
  isWhiteLabel = false

  showRouterLink = true;
  selectedContractId: number;
  selectedServiceAgreementId: number;
  userInfo$: Observable<UserInfo>;
  userType$: Observable<UserType>;

  constructor(
    private store: Store<fromStore.State>,
    private customersStore: Store<fromCustomerStore.State>,
    private route: ActivatedRoute,
    private accountStore: Store<fromAccountStore.State>,
    private contractStore: Store<fromContractsStore.State>,
    private analyzeStore: Store<fromAnalyzeStore.State>,
  ) {}

  ngOnInit() {
    this.allMeteringPoints$ = this.store.select(fromStore.getMeteringPoints);
    this.userInfo$ = this.accountStore.select(fromAccountStore.getUserInfo);
    this.userType$ = this.accountStore.select(fromAccountStore.getUserType);
    this.subs.sink = this.allMeteringPoints$.pipe(filter(x => !!x)).subscribe(meteringPoints => {
      this.hotMeteringPointsCount = meteringPoints.filter(mp => mp.meteringType === MeteringType.Hot).length;
      this.coldMeteringPointsCount = meteringPoints.filter(mp => mp.meteringType === MeteringType.Cold).length;
      this.allMeteringPointsCount = this.hotMeteringPointsCount + this.coldMeteringPointsCount;

      if (this.allMeteringPointsCount === 1 && !this.selectedServiceAgreementId) {
        this.showRouterLink = false;
      }
    });

    this.meteringPoint$ = this.store.select(combinedSelectors.getSelectedMeteringPointWithGroups);

    this.meteringPoints$ = this.store.select(combinedSelectors.getMeteringPointsWithGroups);

    this.customer$ = this.meteringPoint$.pipe(
      filter(mp => !!mp),
      switchMap(mp =>
        this.customersStore.select(fromCustomerStore.getCustomers).pipe(
          filter(x => !!x && x.length > 0),
          map(c => c.find(cs => cs.id === mp.customerId))
        )
      )
    );

    this.subs.sink = this.customer$
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(customer => {
        if(customer) {
          this.isWhiteLabel = customer.isWhiteLabel;
          if(!this.isWhiteLabel) {
           // Load contract if not white label
             this.contract$ = this.meteringPoint$.pipe(
              filter(mp => !!mp),
              switchMap(mp =>
                this.contractStore.select(fromContractsStore.getContracts).pipe(
                  map(contracts => contracts.find(contract => contract.meteringPointId === mp.id)),
                  tap((contract: Contract) => {
                    if (!contract || !contract.isFullObject && !this.isWhiteLabel) {
                      this.contractStore.dispatch(new fromContractsStore.ContractLoad(mp.id));
                    }
                  })
                )
              )
            );
          }
        }
    });

    // this.contract$ = this.meteringPoint$.pipe(
    //   filter(mp => !!mp),
    //   switchMap(mp =>
    //     this.contractStore.select(fromContractsStore.getContracts).pipe(
    //       map(contracts => contracts.find(contract => contract.meteringPointId === mp.id)),
    //       tap((contract: Contract) => {
    //         if (!contract || !contract.isFullObject && !this.isWhiteLabel) {
    //           this.contractStore.dispatch(new fromContractsStore.ContractLoad(mp.id));
    //         }
    //       })
    //     )
    //   )
    // );

    let allPeriodData$ = this.analyzeStore.select(fromAnalyzeStore.getAnalyzePeriodData);
    this.periodData$ = allPeriodData$.pipe(map(sets => sets.find(x => x.meteringType === MeteringType.Hot &&
        x.settingsType === AnalyzeSettingsType.Energy && x.period === DatePeriodType.Month)));

    this.subs.sink = this.route.params
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(params => {
        this.selectedContractId = params.contractId ? +params.contractId : null;
        this.selectedServiceAgreementId = params.agreementId ? +params.agreementId : null;
        let meteringPointId = params.mpId ? +params.mpId : null;

        // If deep linking to meteringpoint replace url for url matching to work in contextual help
        if (params?.tabType) {
          window.history.replaceState(null, '', '/secure/meteringpoints/' + meteringPointId);
        }

        if(this.selectedServiceAgreementId) {
          this.showRouterLink = true;
        }
      });

    this.selectedIndex$ = this.store.select(fromStore.getSelectedMeteringPointTabIndex);

    this.configureSettingsFromRouteIfProvided();
  }

  onTabIndexChange = (ix: number) => {
    switch (ix) {
          case 0:
            this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.Details)); 
            break;
          case 1:
            this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.Metering));
            break;
          case 2:
            this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.FunctionalControl));
            break;
          default:
            break;
        }
  };

  configureSettingsFromRouteIfProvided(){
    let paramTabType = this.route.snapshot.paramMap.get('tabType')?.toLowerCase();

    if (paramTabType) {
      if (paramTabType === 'details') {
        this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.Details));
      } else if (paramTabType === 'metering') {
        this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.Metering));
      } else if (paramTabType === 'functionalcontrol') {
        this.store.dispatch(new fromStore.SelectTabIndexMeteringPoint(MeteringPointTabType.FunctionalControl));
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
